<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useAbility } from '@casl/vue';

import TableAlert from '@/components/alerts/TableAlert.vue';
import KBtn from '@/components/btns/KBtn.vue';
import RequestChip from '@/components/chips/RequestChip.vue';
import TablePageFooter from '@/components/footers/TablePageFooter.vue';
import ExportAsCsvIconBtn from '@/components/iconBtns/ExportAsCsvIconBtn.vue';
import JobIdSearchSearchForm from '@/components/searchForms/JobIdSearchSearchForm.vue';
import JobIdSearchTable from '@/components/tables/JobIdSearchTable.vue';
import FormWrapper from '@/components/wrappers/FormWrapper.vue';
import PageContentWrapper from '@/components/wrappers/PageContentWrapper.vue';
import TablePageShadowWrapper from '@/components/wrappers/TablePageShadowWrapper.vue';
import useCommonRouteQuery from '@/composables/routeQueries/useCommonRouteQuery';
import useCurrentDateString from '@/composables/useCurrentDateString';
import useDownloadCsv from '@/composables/useDownloadCsv';
import { useJobEditDialog } from '@/composables/useJobEditDialog';
import useJobIdSearchView from '@/composables/views/useJobIdSearchView';

const ability = useAbility();
const isStaff = computed(() => ability.can('staff', 'Job'));

const { getLast6MthsNext6MthsRange } = useCurrentDateString();
const { queryDates, queryJobIds } = useCommonRouteQuery({
  queryDates: getLast6MthsNext6MthsRange(),
});
const { isDownloadingCSV, downloadCsv } = useDownloadCsv();
const {
  search,
  refetch,
  initialData,
  isFetchingData,
  isFetchingCountData,
  total,
  totalCount,
  results,
  resultsForCsv,
} = useJobIdSearchView(queryDates, queryJobIds, isStaff);

const route = useRoute();
const jobEditDialog = useJobEditDialog();

function handleEditClick(jobId: number, jobDate: string): void {
  jobEditDialog.open(jobId, jobDate, results.value, refetch);
}
</script>

<template>
  <div :class="$style.jobIdSearchView" data-testid="jobIdSearchView">
    <PageContentWrapper class="mt-2">
      <FormWrapper :class="$style.formWrapper" class="mt-0">
        <h2 class="g-h2">
          Enter required IDs one by one or all together separated by commas
        </h2>
        <JobIdSearchSearchForm
          :initial-data="initialData"
          :loading="isFetchingData"
          :class="$style.form"
          :is-job-ids="!!queryJobIds?.length"
          @submit="search"
        />
      </FormWrapper>
      <div
        v-if="route.query.dates || route.query.job_ids"
        :class="$style.requests"
      >
        <div :class="$style.leftRequests">
          Requested
          <RequestChip param-title="dates" :class="$style.requestChip">
            Date range {{ route.query.dates?.toString().replace(',', '–') }}
          </RequestChip>
          <template v-for="(jobId, index) in queryJobIds" :key="jobId">
            <RequestChip
              param-title="job_ids"
              :param-value="
                (queryJobIds || [])
                  .filter((_jobId) => _jobId !== jobId)
                  .join(',')
              "
              :class="$style.requestChip"
            >
              Job ID {{ jobId }}
            </RequestChip>
          </template>
        </div>
        <KBtn variant="text" :to="{ name: route.name }">
          Clear all requests
        </KBtn>
      </div>
      <TableAlert
        v-if="!isFetchingData && total === 200"
        :class="$style.tableAlert"
      >
        Only 200 jobs are shown. Please, specify your searching request.
        <template v-if="isFetchingCountData">
          <br />
          <v-icon icon="mdi-loading" class="animate-spin"></v-icon>
        </template>
        <template v-else-if="totalCount">
          <br />
          Request result is {{ totalCount }} jobs.
        </template>
      </TableAlert>
      <TablePageShadowWrapper :class="$style.tablePageShadowWrapper">
        <JobIdSearchTable
          :loading="isFetchingData"
          :data="results"
          :is-staff="isStaff"
          @edit-click="handleEditClick"
        />
        <TablePageFooter v-if="results?.length" :total="total" no-pages>
          <ExportAsCsvIconBtn
            :disabled="isFetchingData || !results?.length"
            :loading="isDownloadingCSV"
            @click="downloadCsv(resultsForCsv, 'jobIdSearch')"
          />
        </TablePageFooter>
      </TablePageShadowWrapper>
    </PageContentWrapper>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.jobIdSearchView {
}
  .formWrapper {
    padding: 0.5rem 1.5rem 0.5rem 1.5rem;
    margin: 2rem 0;
  }
  .tablePageShadowWrapper {
  }
  .requests {
    display: flex;
    align-items: start;
    justify-content: space-between;
    margin-bottom: 14px;
  }
    .leftRequests {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
    }
      .requestChip:first-child {
        margin-left: 8px;
      }
      .requestChip {
        margin-right: 8px;
        margin-top: 6px;
        margin-bottom: 6px;
      }
  .requests + .tableAlert {
    margin-top: -7px;
  }
  .tableAlert {
    margin-bottom: 14px;
  }
</style>
